/**
 * Single source of truth for the audit-trail taxonomy: the action vocabulary and its
 * labels, the workflow stages entries are recorded against, and the change-type
 * grouping the audit history dialog filters on.
 *
 * `action` and `stage` are unconstrained strings server-side, so this module is the
 * only place the vocabulary is defined. Adding an action means editing this file alone.
 */

export type AuditAction =
  // Extraction / Quality Assurance
  | "edit_template"
  | "edit_prompt"
  | "run_extract"
  | "reprocess"
  | "reprocess_selected"
  | "change_template"
  | "review"
  | "mark_reviewed"
  | "mark_pending"
  // Report Writing
  | "edit_section"
  | "rename_section"
  | "add_source"
  | "remove_source"
  | "generate_section"
  | "regenerate_section";

/** Workflow step an entry is recorded against — the step keys from `projectTypeConfig`. */
export type AuditStage =
  | "Evidence Gathering"
  | "Outline Creation"
  | "Extraction"
  | "Quality Assurance"
  | "Report Writing";

/** Keyed by `string` rather than `AuditAction` so unknown historical actions fall back to their raw value. */
export const ACTION_LABELS: Record<string, string> = {
  edit_template: "Edit Template",
  edit_prompt: "Edit Prompt",
  run_extract: "Run Extract",
  reprocess: "Reprocess",
  reprocess_selected: "Reprocess Selected",
  change_template: "Change Template",
  review: "Review",
  mark_reviewed: "Marked Reviewed",
  mark_pending: "Marked Pending",
  edit_section: "Edit Section",
  rename_section: "Rename Section",
  add_source: "Add Source",
  remove_source: "Remove Source",
  generate_section: "Generate Section",
  regenerate_section: "Regenerate Section",
};

/** Order of the Action filter dropdown. */
export const ACTION_OPTIONS: AuditAction[] = [
  "edit_template",
  "edit_prompt",
  "run_extract",
  "reprocess",
  "reprocess_selected",
  "change_template",
  "review",
  "mark_reviewed",
  "mark_pending",
  "edit_section",
  "rename_section",
  "add_source",
  "remove_source",
  "generate_section",
  "regenerate_section",
];

/**
 * `entity_type` for a report/outline section. Follows the existing table-name convention
 * used by the Extraction and Quality Assurance entries.
 */
export const AUDIT_ENTITY_PROMPT_LIBRARY = "project_prompt_library";

export const STAGE_BADGE_VARIANT: Record<string, "brand" | "info" | "warning"> = {
  Extraction: "brand",
  "Quality Assurance": "warning",
  "Report Writing": "info",
};

/**
 * Coarse grouping behind the dialog's Change Type filter. Applied client-side over the
 * fetched entries, exactly as the Action and User filters are, so it needs no server
 * support and classifies existing entries for free.
 */
export type AuditChangeType = "Content" | "Sources" | "Generation" | "Review" | "Configuration";

export const CHANGE_TYPE_ACTIONS: Record<AuditChangeType, AuditAction[]> = {
  Content: ["edit_template", "edit_prompt", "edit_section", "rename_section"],
  Sources: ["add_source", "remove_source"],
  Generation: ["run_extract", "reprocess", "reprocess_selected", "generate_section", "regenerate_section"],
  Review: ["review", "mark_reviewed", "mark_pending"],
  Configuration: ["change_template"],
};

/**
 * Upper bound on the entries the dialog fetches for a stage. The change-type, action and
 * user filters are applied client-side, so they can only ever narrow what was fetched;
 * the dialog says so when it hits this bound rather than silently showing a partial trail.
 */
export const AUDIT_ENTRY_LIMIT = 1000;

/** Order of the Change Type filter dropdown. */
export const CHANGE_TYPE_OPTIONS: AuditChangeType[] = [
  "Content",
  "Sources",
  "Generation",
  "Review",
  "Configuration",
];
